import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Search,
  Package,
  User,
  Calendar,
  Truck,
  Plus,
  Edit3,
  Save,
  X,
  Mail,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  Clock,
  Minus,
  ExternalLink,
  Volume2,
  VolumeX,
  ScanLine,
  Layers,
  ChevronDown
} from 'lucide-react';
import InboundContext from './InboundContext';
import moment from 'moment';
import SendEmailInbound from './SendEmailInbound';
import NicknameModal from './NicknameModal';
import { Button, Input, Badge, Card, getStatusConfig } from './SharedComponents';

const API_URL = import.meta.env.VITE_API_URL;
const YYZ_API_URL = 'https://app.yyzprep.ca/api/shipments/inbound';
const API_TOKEN = 'Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq';


function InboundDetail() {
  const { id } = useParams(); 
  const [inboundInfo, setInboundInfo] = useState(null);
  const [inboundItems, setInboundItems] = useState([]);
  const { setInboundData, selectedNickname } = useContext(InboundContext);
  const [orderType, setOrderType] = useState(''); 
  const [inboundStatus, setInboundStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [client, setClient] = useState(null);
  const [changeHistory, setChangeHistory] = useState([]);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isContactSelectModalOpen, setIsContactSelectModalOpen] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [selectedContactEmail, setSelectedContactEmail] = useState('');
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [isEditingInternalNotes, setIsEditingInternalNotes] = useState(false);
  
  // Shelf management
  const [allShelfCode, setAllShelfCode] = useState('');
  const [openShelfDialog, setOpenShelfDialog] = useState(false);
  const [dialogQuantities, setDialogQuantities] = useState({});
  const [recentShelves, setRecentShelves] = useState([]);
  const [shelfQuantities, setShelfQuantities] = useState({});
  const [quickAddInputs, setQuickAddInputs] = useState({});
  const [shelfSuccessAnimation, setShelfSuccessAnimation] = useState({});
  const [hoveredImage, setHoveredImage] = useState(null);
  const [actualInputValues, setActualInputValues] = useState({});
  const [focusedShelfInput, setFocusedShelfInput] = useState(null);
  const [shelfData, setShelfData] = useState({});
  const [editingShelf, setEditingShelf] = useState(null);
  const [editShelfQty, setEditShelfQty] = useState('');

  // Notifications
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Banner for mark as received
  const [showReceivedBanner, setShowReceivedBanner] = useState(false);

  // Item status filter
  const [statusFilter, setStatusFilter] = useState('all');

  // Track items that just became "received" but user hasn't confirmed they're done
  // This prevents items from disappearing before user can add shelving info
  const [pendingConfirmation, setPendingConfirmation] = useState(new Set());

  // Highlighted item from scan/search
  const [highlightedItemId, setHighlightedItemId] = useState(null);

  // Batch shelf mode
  const [batchShelfMode, setBatchShelfMode] = useState(false);
  const [batchShelfCode, setBatchShelfCode] = useState('');
  const [selectedForBatch, setSelectedForBatch] = useState(new Set());

  // Sound feedback
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('wms_sound_enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4000);
  };

  // Sound feedback functions
  const playSound = (type) => {
    if (!soundEnabled) return;

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    if (type === 'success') {
      oscillator.frequency.value = 800;
      gainNode.gain.value = 0.1;
      oscillator.start();
      setTimeout(() => {
        oscillator.frequency.value = 1000;
      }, 100);
      setTimeout(() => oscillator.stop(), 200);
    } else if (type === 'error') {
      oscillator.frequency.value = 300;
      gainNode.gain.value = 0.1;
      oscillator.start();
      setTimeout(() => oscillator.stop(), 300);
    } else if (type === 'complete') {
      oscillator.frequency.value = 600;
      gainNode.gain.value = 0.1;
      oscillator.start();
      setTimeout(() => { oscillator.frequency.value = 800; }, 100);
      setTimeout(() => { oscillator.frequency.value = 1000; }, 200);
      setTimeout(() => oscillator.stop(), 300);
    }
  };

  // Toggle sound and persist preference
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('wms_sound_enabled', JSON.stringify(newValue));
    if (newValue) playSound('success');
  };

  // Scan-to-find function - finds item by barcode/identifier and scrolls to it
  const handleScanSearch = (searchValue) => {
    const searchTerm = searchValue.trim().toUpperCase();
    if (!searchTerm) return false;

    // Search through items for matching identifiers (exact or partial match)
    const foundItem = inboundItems.find(item => {
      const searchableIds = item.item?.searchableIdentifiers?.toUpperCase() || '';
      const sku = item.item?.merchant_sku?.toUpperCase() || '';
      const asin = item.item?.asin?.toUpperCase() || '';
      const fnsku = item.item?.fnsku?.toUpperCase() || '';
      const title = item.item?.title?.toUpperCase() || '';

      // Check for exact barcode matches first (searchableIdentifiers contains comma-separated barcodes)
      const barcodes = searchableIds.split(',').map(b => b.trim());
      if (barcodes.includes(searchTerm)) return true;

      // Then check other identifiers
      return sku.includes(searchTerm) ||
             asin === searchTerm ||
             fnsku === searchTerm ||
             title.includes(searchTerm);
    });

    if (foundItem) {
      setHighlightedItemId(foundItem.id);
      setStatusFilter('all'); // Show all items so we can see the found one
      // Keep the search query - the filter now includes searchableIdentifiers so item will be visible

      // Scroll to the item and focus the actual input
      setTimeout(() => {
        const element = document.getElementById(`item-row-${foundItem.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Focus the actual quantity input WITHOUT triggering blur on current
          setTimeout(() => {
            const actualInput = document.getElementById(`actual-input-${inboundItems.findIndex(i => i.id === foundItem.id)}`);
            if (actualInput) {
              actualInput.focus();
              actualInput.select();
            }
          }, 100);
        }
      }, 50);

      playSound('success');
      showNotification(`Found: ${foundItem.item?.title?.substring(0, 50)}...`);

      // Clear highlight after 5 seconds
      setTimeout(() => setHighlightedItemId(null), 5000);
      return true;
    }

    return false; // Not found, let normal search handle it
  };

  // Batch shelf assignment
  const handleBatchShelfAssignment = async () => {
    if (!batchShelfCode || selectedForBatch.size === 0) {
      showNotification('Please enter a shelf code and select items', 'error');
      return;
    }

    try {
      showNotification(`Adding ${selectedForBatch.size} items to shelf ${batchShelfCode}...`, 'info');

      for (const itemId of selectedForBatch) {
        const item = inboundItems.find(i => i.id === itemId);
        if (!item) continue;

        const quantity = item.actual?.quantity || item.expected?.quantity || 0;
        if (quantity <= 0) continue;

        await fetch(`${API_URL}/api/add_or_update_shelving_qty`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            key: `${batchShelfCode}-${item.item.merchant_sku}`,
            qty: quantity,
            sku: item.item.merchant_sku,
            shelfCode: batchShelfCode,
            employee: selectedNickname || 'SYSTEM',
            shipmentId: inboundInfo.id,
            action_type: "INBOUND_TOOL_BATCH_SHELF",
            team_id: inboundInfo.team_id
          })
        });
      }

      await fetchShelfData(inboundItems);
      addToRecentShelves(batchShelfCode);
      playSound('complete');
      showNotification(`Successfully added ${selectedForBatch.size} items to shelf ${batchShelfCode}`);
      setSelectedForBatch(new Set());
      setBatchShelfMode(false);
      setBatchShelfCode('');
    } catch (error) {
      console.error('Error in batch shelf assignment:', error);
      playSound('error');
      showNotification('Failed to add items to shelf', 'error');
    }
  };

  // Toggle item selection for batch mode
  const toggleBatchSelection = (itemId) => {
    setSelectedForBatch(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Select all visible items for batch
  const selectAllForBatch = () => {
    const allIds = new Set(filteredItems.map(item => item.id));
    setSelectedForBatch(allIds);
  };

  // Clear batch selection
  const clearBatchSelection = () => {
    setSelectedForBatch(new Set());
  };

  // Check if all items are received (actual = expected)
  const checkAllItemsReceived = () => {
    if (!inboundInfo || !inboundItems || inboundItems.length === 0) {
      return;
    }

    const allItemsReceived = inboundItems.every(item =>
      item.expected?.quantity === item.actual?.quantity
    );

    if (allItemsReceived && inboundStatus.toLowerCase() !== 'received') {
      setShowReceivedBanner(true);
    } else {
      setShowReceivedBanner(false);
    }
  };

  // Mark shipment as received via YYZ API
  const handleMarkShipmentAsReceived = async () => {
    try {
      showNotification('Marking shipment as received...', 'info');

      const response = await fetch(`${YYZ_API_URL}/${id}/receive?api_token=${API_TOKEN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'received' })
      });

      if (response.ok) {
        showNotification('Shipment marked as received successfully', 'success');
        setShowReceivedBanner(false);
        await fetchInboundData(); // Refresh data
      } else {
        throw new Error('Failed to update shipment status');
      }
    } catch (error) {
      console.error('Error marking shipment as received:', error);
      showNotification('Failed to mark shipment as received', 'error');
    }
  };

  // Mark shipment as shipped via YYZ API (submit with no_tracking)
  const markShipmentAsShipped = async () => {
    try {
      // Call YYZ API /submit endpoint to mark as shipped
      const yyzResponse = await fetch(`${YYZ_API_URL}/${id}/submit?api_token=${API_TOKEN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selected_carrier: 'no_tracking',
          tracking_numbers: {}
        })
      });

      if (yyzResponse.ok) {
        const result = await yyzResponse.json();
        console.log(`YYZ API response: ${result.message}`);
      } else {
        console.warn(`YYZ submit failed: ${yyzResponse.status}`);
      }

      // Update local database
      const response = await fetch(`${API_URL}/api/update-inbound-field/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field: 'status', value: 'shipped' }),
      });

      if (response.ok) {
        setInboundStatus('shipped');
        showNotification('Shipment marked as shipped');
        console.log(`Inbound ${id} auto-marked as shipped`);
      }
    } catch (error) {
      console.error('Error auto-marking shipment as shipped:', error);
    }
  };

  // Fetch shelf data using existing API
  const fetchShelfData = async (inboundItems) => {
    try {
      // Extract SKUs from inbound items
      const skus = inboundItems.map(item => item.item.merchant_sku);

      // Fetch shelf data for the extracted SKUs
      const shelfResponse = await fetch(`${API_URL}/api/get_shelving_data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ skus }),
      });

      const shelfDataResult = await shelfResponse.json();

      // Create a map for quick lookup of shelf data by SKU
      const shelfDataMap = shelfDataResult.reduce((acc, item) => {
        if (!acc[item.SKU]) {
          acc[item.SKU] = [];
        }
        acc[item.SKU].push({
          FULL_SHELF_CODE: item.FULL_SHELF_CODE,
          QTY: item.QTY,
          KEY: item.KEY,
        });
        return acc;
      }, {});

      // Update the state with the new shelf data
      setShelfData(shelfDataMap);
      console.log('Fetched shelf data:', shelfDataMap);

    } catch (error) {
      console.error('Error fetching shelf data:', error);
    }
  };

  // Fetch inbound data
  useEffect(() => {
    fetchInboundData();
  }, [id]);

  // Check if all items are received when data changes
  useEffect(() => {
    checkAllItemsReceived();
  }, [inboundItems, inboundStatus]);

  // Update time display every second
  useEffect(() => {
    const interval = setInterval(() => {
      // Force re-render to update "time ago" display
      setChangeHistory(prev => [...prev]);
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  const fetchInboundData = async () => {
    try {
      // Fetch inbound information using yyzprep.ca API like the old implementation
      const infoResponse = await fetch(`${YYZ_API_URL}/${id}?api_token=${API_TOKEN}`);
      const inboundInfoData = await infoResponse.json();
      setInboundInfo(inboundInfoData.shipment);
      setOrderType(inboundInfoData.shipment.order_type || '');
      setInboundStatus(inboundInfoData.shipment.status?.toLowerCase() || '');
      setNotes(inboundInfoData.shipment.notes || '');
      setInternalNotes(inboundInfoData.shipment.internal_notes || '');

      // Fetch inbound items using yyzprep.ca API like the old implementation  
      const itemsResponse = await fetch(`${YYZ_API_URL}/${id}/items?api_token=${API_TOKEN}`);
      const inboundItemsData = await itemsResponse.json();
      setInboundItems(inboundItemsData.items);
      
      // Fetch and update shelf data using the existing method
      await fetchShelfData(inboundItemsData.items);

      // Fetch client info if team_id is available
      if (inboundInfoData.shipment.team_id) {
        await fetchClientInfo(inboundInfoData.shipment.team_id);
      }
      
    } catch (error) {
      console.error('Error fetching inbound data:', error);
      showNotification('Failed to load inbound data', 'error');
    }
  };

  // Load change history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem(`inbound_changes_${id}`);
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        // Filter out changes older than 30 days
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
        const validHistory = parsed.filter(change => change.timestamp > thirtyDaysAgo);
        setChangeHistory(validHistory);
      } catch (error) {
        console.error('Error loading change history:', error);
      }
    }
  }, [id]);

  // Add change to history and save to localStorage
  const addChangeToHistory = (type, sku, oldValue, newValue, productTitle) => {
    const difference = newValue - oldValue;
    const newChange = {
      id: Date.now(),
      timestamp: Date.now(),
      type, // 'direct' or 'partial'
      sku,
      productTitle: productTitle?.substring(0, 30) + (productTitle?.length > 30 ? '...' : ''),
      oldValue,
      newValue,
      change: type === 'partial' 
        ? (difference > 0 ? `+${difference}` : `${difference}`) 
        : `${oldValue} → ${newValue}`
    };

    setChangeHistory(prev => {
      const updated = [newChange, ...prev.slice(0, 14)]; // Keep more changes to support 3 per SKU
      
      // Save to localStorage
      try {
        localStorage.setItem(`inbound_changes_${id}`, JSON.stringify(updated));
      } catch (error) {
        console.error('Error saving change history:', error);
      }
      
      return updated;
    });
  };

  // Get recent changes for a specific SKU
  const getRecentChangesForSku = (sku, limit = 3) => {
    return changeHistory
      .filter(change => change.sku === sku)
      .slice(0, limit);
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // Fetch all clients for contact selection
  const fetchAllClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/get_clients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const clients = await response.json();
      setAllClients(clients);
    } catch (error) {
      console.error('Error fetching all clients:', error);
    }
  };

  // Fetch client information
  const fetchClientInfo = async (teamId) => {
    try {
      const response = await fetch(`${API_URL}/api/get_clients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const clients = await response.json();
      setAllClients(clients); // Store all clients for contact selection

      // Find the client with the matching team_id
      const matchedClient = clients.find(client => client.id === teamId);

      if (matchedClient) {
        setClient(matchedClient);
        setSelectedContactEmail(matchedClient.email || ''); // Set default email
      } else {
        console.error('Client not found');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      // Set a minimal client object so the app doesn't break
      setClient({ id: teamId, name: 'Unknown Client' });
    }
  };


  // Helper function to get item status
  const getItemStatus = (item) => {
    const expected = item.expected?.quantity || 0;
    const actual = item.actual?.quantity || 0;

    if (actual === 0) return 'not_started';
    if (actual < expected) return 'partial';
    if (actual === expected) return 'received';
    if (actual > expected) return 'over';
    return 'unknown';
  };

  // Get counts for each status
  const statusCounts = inboundItems.reduce((acc, item) => {
    const status = getItemStatus(item);
    acc[status] = (acc[status] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    // Also track "pending" which combines not_started + partial
    if (status === 'not_started' || status === 'partial') {
      acc.pending = (acc.pending || 0) + 1;
    }
    return acc;
  }, { all: 0, pending: 0, not_started: 0, partial: 0, received: 0, over: 0 });

  // Helper function to highlight matching text
  const highlightMatch = (text, query) => {
    if (!query || !text) return text;
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);
    if (index === -1) return text;

    return (
      <>
        {text.substring(0, index)}
        <mark className="bg-yellow-200 px-0.5 rounded">{text.substring(index, index + query.length)}</mark>
        {text.substring(index + query.length)}
      </>
    );
  };

  // Check if a field matches the search query
  const fieldMatches = (text, query) => {
    if (!query || !text) return false;
    return text.toLowerCase().includes(query.toLowerCase());
  };

  // Filter items based on search AND status filter
  const filteredItems = inboundItems.filter(item => {
    // First apply search filter
    const query = searchQuery.toLowerCase();
    const matchesSearch = (
      item.item?.title?.toLowerCase().includes(query) ||
      item.item?.merchant_sku?.toLowerCase().includes(query) ||
      item.item?.asin?.toLowerCase().includes(query) ||
      item.item?.fnsku?.toLowerCase().includes(query) ||
      item.item?.searchableIdentifiers?.toLowerCase().includes(query)
    );

    if (!matchesSearch) return false;

    // Then apply status filter
    if (statusFilter === 'all') return true;

    const itemStatus = getItemStatus(item);

    // Keep items visible if they're pending confirmation (even if they would normally be filtered out)
    // This allows users to add shelving info before the item disappears
    if (pendingConfirmation.has(item.id)) {
      return true;
    }

    if (statusFilter === 'pending') {
      return itemStatus === 'not_started' || itemStatus === 'partial';
    }

    return itemStatus === statusFilter;
  });

  // Handle individual shelf addition
  const handleAddToShelf = async (index, shelfCode, quantity) => {
    try {
      const item = inboundItems[index];
      
      const response = await fetch(`${API_URL}/api/add_or_update_shelving_qty`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: `${shelfCode}-${item.item.merchant_sku}`,
          qty: parseInt(quantity),
          sku: item.item.merchant_sku,
          shelfCode: shelfCode,
          employee: selectedNickname || 'SYSTEM',
          shipmentId: inboundInfo.id,
          action_type: "INBOUND_TOOL_ADD_INDIVIDUAL",
          team_id: inboundInfo.team_id
        })
      });

      const result = await response.json();

      if (result.success) {
        playSound('success');
        showNotification(`Added ${quantity} to shelf ${shelfCode}`);
        addToRecentShelves(shelfCode, quantity);
        // Clear the inputs after successful addition
        setShelfQuantities(prev => ({
          ...prev,
          [index]: { code: '', quantity: '' }
        }));
        // Trigger success animation
        setShelfSuccessAnimation(prev => ({ ...prev, [index]: true }));
        setTimeout(() => {
          setShelfSuccessAnimation(prev => ({ ...prev, [index]: false }));
        }, 1000);
        // Refresh shelf data
        await fetchShelfData(inboundItems);
      } else {
        throw new Error('Failed to add to shelf');
      }
    } catch (error) {
      console.error('Error adding to shelf:', error);
      playSound('error');
      showNotification('Failed to add to shelf', 'error');
    }
  };

  // Handle bulk shelf addition
  const handleAddAllToShelf = async () => {
    try {
      showNotification('Saving...', 'info');
      
      for (let index = 0; index < inboundItems.length; index++) {
        const item = inboundItems[index];
        // Use the same logic as the input fields and button counter
        const inputQuantity = dialogQuantities[index] !== undefined ? dialogQuantities[index] : (item.expected?.quantity || 0);

        // Skip if the quantity is 0 or shelf code is empty
        if (inputQuantity <= 0 || !allShelfCode) continue;

        const response = await fetch(`${API_URL}/api/add_or_update_shelving_qty`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            key: `${allShelfCode}-${item.item.merchant_sku}`,
            qty: inputQuantity,
            sku: item.item.merchant_sku,
            shelfCode: allShelfCode,
            shipmentId: inboundInfo.id,
            action_type: "INBOUND_TOOL_ADDALLTOSHELF",
            employee: selectedNickname || 'SYSTEM',
            team_id: inboundInfo.team_id
          })
        });

        const result = await response.json();

        if (!result.success) {
          console.error('Failed to update shelving quantity:', result.error);
          throw new Error('Failed to update shelving quantity');
        }
      }

      await fetchShelfData(inboundItems);
      addToRecentShelves(allShelfCode);
      showNotification('All items added to shelf successfully', 'success');
      setOpenShelfDialog(false);
      setAllShelfCode('');
      setDialogQuantities({});

    } catch (error) {
      console.error('Error adding all to shelf:', error);
      showNotification('Failed to add all to shelf', 'error');
    }
  };

  // Handle updating shelf quantity or removing from shelf
  const handleUpdateShelfQty = async (shelf, newQty) => {
    try {
      const quantity = parseInt(newQty) || 0;
      
      if (quantity === 0) {
        // Remove from shelf by setting quantity to 0
        showNotification('Removing from shelf...', 'info');
      } else {
        showNotification('Updating shelf quantity...', 'info');
      }

      const response = await fetch(`${API_URL}/api/update_shelving_qty`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: shelf.KEY,
          shelfCode: shelf.FULL_SHELF_CODE,
          qty: quantity,
          employee: selectedNickname || 'SYSTEM',
          sku: shelf.SKU || shelf.FULL_SHELF_CODE.split('-').pop(),
          action_type: "INBOUND_TOOL_EDITSETSHELFQTY",
          shipmentId: inboundInfo?.id,
          team_id: inboundInfo?.team_id
        })
      });

      const result = await response.json();

      if (result.success) {
        if (quantity === 0) {
          showNotification('Removed from shelf', 'success');
        } else {
          showNotification('Shelf quantity updated', 'success');
        }
        setEditingShelf(null);
        await fetchShelfData(inboundItems);
      } else {
        throw new Error('Failed to update shelf quantity');
      }
    } catch (error) {
      console.error('Error updating shelf quantity:', error);
      showNotification('Failed to update shelf quantity', 'error');
      setEditingShelf(null);
    }
  };

  // Recent shelves management with quantity tracking
  const addToRecentShelves = (shelfCode, quantity = null) => {
    setRecentShelves(prev => {
      const shelfData = quantity ? { code: shelfCode, lastQty: quantity } : shelfCode;
      // Handle both old string format and new object format
      const updated = [shelfData, ...prev.filter(s => {
        const code = typeof s === 'string' ? s : s.code;
        return code !== shelfCode;
      })].slice(0, 5);
      return updated;
    });
  };

  // Handle notes update - using YYZ API like the old implementation
  const updateNotes = async (field, value) => {
    try {
      showNotification('Saving notes...', 'info');

      const response = await fetch(`${YYZ_API_URL}/${id}?api_token=${API_TOKEN}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          [field]: value,
        }),
      });

      if (response.ok) {
        showNotification('Notes updated successfully');
        if (field === 'notes') setIsEditingNotes(false);
        if (field === 'internal_notes') setIsEditingInternalNotes(false);
      } else {
        throw new Error('Failed to update notes');
      }
    } catch (error) {
      console.error('Error updating notes:', error);
      showNotification('Failed to update notes', 'error');
    }
  };

  // Set all actual quantities to expected values and save in bulk
  const handleSetAllActualToExpected = async () => {
    try {
      showNotification('Setting all actual to expected...', 'info');
      
      // Update local state optimistically
      const updatedItems = inboundItems.map(item => ({
        ...item,
        actual: { ...item.actual, quantity: item.expected?.quantity || 0 }
      }));
      setInboundItems(updatedItems);
      
      // Prepare bulk update for YYZ API
      const bulkUpdates = inboundItems
        .filter((item, index) => (item.actual?.quantity || 0) !== (item.expected?.quantity || 0))
        .map(item => ({
          ...item,
          actual: { ...item.actual, quantity: item.expected?.quantity || 0 }
        }));
      
      if (bulkUpdates.length === 0) {
        showNotification('All items already match expected quantities', 'info');
        return;
      }
      
      // Send bulk update to YYZ API
      const bulkResponse = await fetch(`${YYZ_API_URL}/${id}/bulk-update-items?api_token=${API_TOKEN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: bulkUpdates })
      });
      
      if (bulkResponse.ok) {
        // Add bulk change to history
        bulkUpdates.forEach((item, index) => {
          const originalIndex = inboundItems.findIndex(orig => orig.id === item.id);
          addChangeToHistory(
            'direct',
            item.item?.merchant_sku,
            item.actual?.quantity || 0,
            item.expected?.quantity || 0,
            item.item?.title
          );
        });
        
        showNotification(`Updated ${bulkUpdates.length} items to expected quantities`, 'success');
        await fetchInboundData(); // Refresh to get latest data
      } else {
        throw new Error('Bulk update failed');
      }
    } catch (error) {
      console.error('Error setting all actual to expected:', error);
      showNotification('Failed to set all actual to expected', 'error');
      // Revert optimistic update on error
      await fetchInboundData();
    }
  };

  const handleMarkAllReceived = async () => {
    try {
      const response = await fetch(`${API_URL}/api/mark-all-received/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        showNotification('All items marked as received successfully');
        await fetchInboundData(); // Refresh the data
      } else {
        throw new Error('Failed to mark all as received');
      }
    } catch (error) {
      console.error('Error marking all as received:', error);
      showNotification('Failed to mark all as received', 'error');
    }
  };

  const handleOrderTypeChange = async (value) => {
    setOrderType(value);
    try {
      showNotification('Updating order type...', 'info');
      
      // Update the local database
      const response = await fetch(`${API_URL}/api/update-inbound-field/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          field: 'order_type',
          value: value
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update order type in database');
      }

      // If setting to OA, apply OA fee to all items
      if (value === 'OA') {
        showNotification('Applying OA fees to items...', 'info');

        try {
          const oaResponse = await fetch(`${API_URL}/api/apply-oa-fees/${id}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!oaResponse.ok) {
            throw new Error('Failed to apply OA fees');
          }

          const oaResult = await oaResponse.json();

          if (oaResult.failureCount > 0) {
            showNotification(`Order type updated. OA fee applied to ${oaResult.successCount} items, ${oaResult.failureCount} failed.`, 'warning');
          } else {
            showNotification(`Order type updated successfully. OA fee applied to all ${oaResult.successCount} items.`, 'success');
          }
        } catch (error) {
          console.error('Error applying OA fees:', error);
          showNotification('Order type updated, but failed to apply OA fees', 'warning');
        }
      } else {
        showNotification('Order type updated successfully', 'success');
      }
      
    } catch (error) {
      console.error('Error updating order type:', error);
      showNotification('Failed to update order type', 'error');
    }
  };

  const handleStatusChange = async (value) => {
    setInboundStatus(value);
    try {
      showNotification('Updating status...', 'info');

      // Update local database
      const response = await fetch(`${API_URL}/api/update-inbound-field/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          field: 'status',
          value: value
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update status in database');
      }

      // If setting to received, also update YYZ API
      if (value === 'received') {
        const yyzResponse = await fetch(`${YYZ_API_URL}/${id}/receive?api_token=${API_TOKEN}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: 'received' })
        });

        if (!yyzResponse.ok) {
          throw new Error('Failed to update status in YYZ API');
        }
      }

      showNotification('Status updated successfully');
      setShowReceivedBanner(false); // Hide banner if it was showing
      await fetchInboundData(); // Refresh data
    } catch (error) {
      console.error('Error updating status:', error);
      showNotification('Failed to update status', 'error');
      // Revert status on error
      setInboundStatus(inboundInfo.status?.toLowerCase() || '');
    }
  };

  // Handle actual quantity update
  const handleActualQuantityChange = async (itemIndex, newQuantity, isPartialAdd = false) => {
    try {
      const item = inboundItems[itemIndex];
      const oldQuantity = item.actual?.quantity || 0;
      const expectedQty = item.expected?.quantity || 0;

      // Check if this change will make the item "received" (actual === expected)
      // and if we're filtering in a way that would hide it
      const willBecomeReceived = newQuantity === expectedQty;
      const wasNotReceived = oldQuantity !== expectedQty;
      const wouldBeFilteredOut = statusFilter === 'pending' || statusFilter === 'not_started' || statusFilter === 'partial';

      // Update local state immediately for responsiveness
      setInboundItems(prev => prev.map((item, index) =>
        index === itemIndex
          ? { ...item, actual: { ...item.actual, quantity: newQuantity } }
          : item
      ));

      // If the item just became "received" and would be filtered out, add to pending confirmation
      // This keeps it visible so user can add shelving info before it disappears
      if (willBecomeReceived && wasNotReceived && wouldBeFilteredOut) {
        setPendingConfirmation(prev => new Set([...prev, item.id]));
      }

      // Use the yyzprep.ca API like the old implementation
      const response = await fetch(`${YYZ_API_URL}/${id}/update-item?api_token=${API_TOKEN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...item,
          actual: { ...item.actual, quantity: newQuantity }
        })
      });

      if (response.ok) {
        // Add to change history
        addChangeToHistory(
          isPartialAdd ? 'partial' : 'direct',
          item.item?.merchant_sku,
          oldQuantity,
          newQuantity,
          item.item?.title
        );

        // Play appropriate sound based on status
        if (newQuantity === expectedQty) {
          playSound('complete');
          showNotification('Item fully received!');
        } else {
          playSound('success');
          showNotification('Actual quantity updated');
        }

        // Auto-mark as shipped if this is the first item being added and status is open
        if (newQuantity > 0 && inboundStatus.toLowerCase() === 'open') {
          await markShipmentAsShipped();
        }

        // Refresh the data to reflect the changes
        await fetchInboundData();
      } else {
        // Revert on failure
        await fetchInboundData();
        throw new Error('Failed to update actual quantity');
      }
    } catch (error) {
      console.error('Error updating actual quantity:', error);
      playSound('error');
      showNotification('Failed to update actual quantity', 'error');
      // Revert on error
      await fetchInboundData();
    }
  };

  // Handle set actual = expected
  const handleSetActualToExpected = async (itemIndex) => {
    const item = inboundItems[itemIndex];
    const expectedQty = item.expected?.quantity || 0;
    await handleActualQuantityChange(itemIndex, expectedQty);
  };

  // Handle quick add to actual quantity (allows negative values)
  const handleQuickAdd = async (itemIndex) => {
    const addValue = parseInt(quickAddInputs[itemIndex] || '0');
    if (addValue !== 0) {
      const item = inboundItems[itemIndex];
      const currentActual = item.actual?.quantity || 0;
      const newTotal = Math.max(0, currentActual + addValue); // Prevent negative totals
      await handleActualQuantityChange(itemIndex, newTotal, true); // true for partial add
      // Clear the quick add input after successful addition
      setQuickAddInputs(prev => ({ ...prev, [itemIndex]: '' }));

      // Pre-fill shelf quantity with the increment (if positive)
      if (addValue > 0) {
        setShelfQuantities(prev => ({
          ...prev,
          [itemIndex]: { ...prev[itemIndex], quantity: addValue.toString() }
        }));
      }
    }
  };

  // Handle confirmation that user is done with a received item
  // This removes it from pendingConfirmation so it can be filtered out normally
  const handleConfirmItemDone = (itemId) => {
    setPendingConfirmation(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
    showNotification('Item marked as done');
  };


  if (!inboundInfo || inboundItems.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin -full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading inbound details...</p>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(inboundStatus);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
        {/* Notification */}
        {notification.show && (
          <div className={`fixed top-4 right-4 z-50 p-4  shadow-lg ${
            notification.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
            notification.type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
            'bg-blue-100 text-blue-800 border border-blue-200'
          }`}>
            {notification.message}
          </div>
        )}

        {/* Compact Header Card */}
        <Card className="p-4 mb-4">
          <div className="flex flex-wrap items-start gap-4">
            {/* Left Section - Shipment Info */}
            <div className="flex-1 min-w-[250px]">
              {/* Title and Status */}
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{inboundInfo.name}</div>
                  <a
                    href={`https://app.yyzprep.ca/staff/shipments/inbound/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-0.5 inline-flex items-center gap-1 transition-colors"
                  >
                    Shipment #{id}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <Badge variant={statusConfig.variant} className="px-2 py-1">
                  <statusConfig.icon className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">{statusConfig.label}</span>
                </Badge>
              </div>
              
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div className="flex items-center gap-1.5">
                  <User className="w-3 h-3 text-gray-400" />
                  <span className="text-gray-600">Client:</span>
                  <span className="font-medium text-gray-900">{client?.name || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Package className="w-3 h-3 text-gray-400" />
                  <span className="text-gray-600">SKUs:</span>
                  <span className="font-medium text-gray-900">{inboundInfo.sku_count}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-gray-400" />
                  <span className="text-gray-600">Received:</span>
                  <span className="font-medium text-gray-900">{inboundInfo.received_quantity} / {inboundInfo.expected_quantity}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3 h-3 text-gray-400" />
                  <span className="text-gray-600">Created:</span>
                  <span className="font-medium text-gray-900">{moment(inboundInfo.created_at).format('MMM D, YYYY')}</span>
                </div>
              </div>

              {/* Dropdowns */}
              <div className="flex gap-2 mt-3">
                <div className="flex-1">
                  <label className="text-xs text-gray-600 mb-1 block">Order Type</label>
                  <select
                    value={orderType}
                    onChange={(e) => handleOrderTypeChange(e.target.value)}
                    className="w-full px-3 py-1.5 text-xs border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Type</option>
                    <option value="OA">OA (From Retailers)</option>
                    <option value="Not OA">Not OA (Wholesale)</option>
                    <option value="Amazon Removal Receiving">Amazon Removal</option>
                  </select>
                </div>
                
                <div className="flex-1">
                  <label className="text-xs text-gray-600 mb-1 block">Status</label>
                  <select
                    value={inboundStatus}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={inboundStatus === 'received'}
                    className={`w-full px-3 py-1.5 text-xs border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      inboundStatus === 'received'
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : 'bg-white text-gray-900'
                    }`}
                  >
                    <option value="open" disabled={inboundStatus === 'received'}>Open</option>
                    <option value="shipped" disabled={inboundStatus === 'received'}>Shipped</option>
                    <option value="received">Received</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Middle Section - Notes (Collapsible) */}
            <div className="flex-1 min-w-[300px] max-w-[500px]">
              <div className="grid grid-cols-2 gap-2">
                {/* Public Notes */}
                <div className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-700">Notes</span>
                    <button
                      onClick={() => setIsEditingNotes(!isEditingNotes)}
                      className="p-0.5 hover:bg-gray-100 transition-colors"
                    >
                      <Edit3 className="w-3 h-3 text-gray-500" />
                    </button>
                  </div>
                  {isEditingNotes ? (
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      onBlur={() => updateNotes('notes', notes)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 bg-white focus:outline-none focus:ring-1 focus:ring-gray-500"
                      rows={2}
                    />
                  ) : (
                    <p className="text-xs text-gray-600 line-clamp-2 min-h-[2rem]">
                      {notes || 'No notes'}
                    </p>
                  )}
                </div>

                {/* Internal Notes */}
                <div className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-yellow-700">Internal</span>
                    <button
                      onClick={() => setIsEditingInternalNotes(!isEditingInternalNotes)}
                      className="p-0.5 hover:bg-yellow-100 transition-colors"
                    >
                      <Edit3 className="w-3 h-3 text-yellow-600" />
                    </button>
                  </div>
                  {isEditingInternalNotes ? (
                    <textarea
                      value={internalNotes}
                      onChange={(e) => setInternalNotes(e.target.value)}
                      onBlur={() => updateNotes('internal_notes', internalNotes)}
                      className="w-full px-2 py-1 text-xs border border-yellow-300 bg-yellow-50 focus:outline-none focus:ring-1 focus:ring-yellow-500"
                      rows={2}
                    />
                  ) : (
                    <p className="text-xs text-gray-600 line-clamp-2 min-h-[2rem] bg-yellow-50 px-2 py-1 border border-yellow-200">
                      {internalNotes || 'No internal notes'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Section - Quick Actions */}
            <div className="flex flex-col gap-2 min-w-[150px]">
              <Button
                onClick={handleSetAllActualToExpected}
                variant="outline"
                size="sm"
                className="w-full text-xs px-3 py-1.5 justify-start hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-all duration-200 hover:shadow-sm"
              >
                <CheckCircle2 className="w-3 h-3 mr-2 transition-colors duration-200" />
                Set Actual = Expected
              </Button>
              <Button
                onClick={() => {
                  // Pre-populate with current client's info
                  if (client?.email) {
                    setSelectedContactEmail(client.email);
                    setContactSearchQuery(`${client.name} (${client.email})`);
                  }
                  setIsContactSelectModalOpen(true);
                }}
                variant="outline"
                size="sm"
                className="w-full text-xs px-3 py-1.5 justify-start hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all duration-200 hover:shadow-sm"
              >
                <Mail className="w-3 h-3 mr-2 transition-colors duration-200" />
                Contact Client
              </Button>
              <Button
                onClick={() => setOpenShelfDialog(true)}
                variant="outline"
                size="sm"
                className="w-full text-xs px-3 py-1.5 justify-start hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700 transition-all duration-200 hover:shadow-sm"
              >
                <Plus className="w-3 h-3 mr-2 transition-colors duration-200" />
                Add All To Shelf
              </Button>
            </div>
          </div>
        </Card>

        {/* Mark as Received Banner */}
        {showReceivedBanner && (
          <Card className="p-4 mb-4 border-green-200 bg-green-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    All items expected are received!
                  </p>
                  <p className="text-xs text-green-600">
                    Would you like to mark this shipment as received?
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={handleMarkShipmentAsReceived}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Mark as Received
                </Button>
                <Button
                  onClick={() => setShowReceivedBanner(false)}
                  variant="outline"
                  size="sm"
                  className="text-green-600 border-green-300 hover:bg-green-100"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Batch Shelf Mode Bar */}
        {batchShelfMode ? (
          <Card className="p-3 mb-4 border-2 border-purple-200 bg-purple-50">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-800">Batch Shelf Mode:</span>
              </div>
              <div className="flex-1 max-w-xs">
                <input
                  type="text"
                  placeholder="Enter shelf code (e.g., P-01-01)"
                  value={batchShelfCode}
                  onChange={(e) => setBatchShelfCode(e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 text-sm border-2 border-purple-300 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                />
              </div>
              <div className="flex items-center gap-2 text-sm text-purple-700">
                <span className="font-medium">{selectedForBatch.size}</span> items selected
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={selectAllForBatch}
                  variant="outline"
                  size="sm"
                  className="text-purple-600 border-purple-300 hover:bg-purple-100"
                >
                  Select All
                </Button>
                <Button
                  onClick={clearBatchSelection}
                  variant="outline"
                  size="sm"
                  className="text-purple-600 border-purple-300 hover:bg-purple-100"
                >
                  Clear
                </Button>
                <Button
                  onClick={handleBatchShelfAssignment}
                  disabled={!batchShelfCode || selectedForBatch.size === 0}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Shelf {selectedForBatch.size} Items to {batchShelfCode || '...'}
                </Button>
                <Button
                  onClick={() => {
                    setBatchShelfMode(false);
                    setBatchShelfCode('');
                    setSelectedForBatch(new Set());
                  }}
                  variant="outline"
                  size="sm"
                  className="text-gray-600 border-gray-300"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {/* Recent shelves for batch mode */}
            {recentShelves.length > 0 && (
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-purple-200">
                <span className="text-xs text-purple-600">Recent:</span>
                {recentShelves.slice(0, 5).map((shelf, idx) => {
                  const shelfCode = typeof shelf === 'string' ? shelf : shelf.code;
                  return (
                    <button
                      key={idx}
                      onClick={() => setBatchShelfCode(shelfCode)}
                      className="px-2 py-1 text-xs bg-purple-100 text-purple-700 hover:bg-purple-200 font-mono"
                    >
                      {shelfCode}
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        ) : (
          <div className="flex justify-end mb-2">
            <Button
              onClick={() => setBatchShelfMode(true)}
              variant="outline"
              size="sm"
              className="text-purple-600 border-purple-300 hover:bg-purple-50"
            >
              <Layers className="w-4 h-4 mr-1" />
              Enter Batch Shelf Mode
            </Button>
          </div>
        )}

        {/* Items Table */}
        <Card className="overflow-hidden mb-24">
          <div className="px-6 py-3 border-b border-gray-200">
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-900">Progress</span>
                  <span className="text-sm text-gray-600">
                    {statusCounts.received + (statusCounts.over || 0)} / {statusCounts.all} items received
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {statusCounts.all > 0 ? Math.round(((statusCounts.received + (statusCounts.over || 0)) / statusCounts.all) * 100) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div className="h-full flex">
                  <div
                    className="bg-green-500 transition-all duration-500"
                    style={{ width: `${statusCounts.all > 0 ? ((statusCounts.received) / statusCounts.all) * 100 : 0}%` }}
                  />
                  <div
                    className="bg-purple-500 transition-all duration-500"
                    style={{ width: `${statusCounts.all > 0 ? ((statusCounts.over || 0) / statusCounts.all) * 100 : 0}%` }}
                  />
                  <div
                    className="bg-blue-400 transition-all duration-500"
                    style={{ width: `${statusCounts.all > 0 ? ((statusCounts.partial || 0) / statusCounts.all) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full"></span> Received ({statusCounts.received})</span>
                {statusCounts.over > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 bg-purple-500 rounded-full"></span> Over ({statusCounts.over})</span>}
                {statusCounts.partial > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-400 rounded-full"></span> Partial ({statusCounts.partial})</span>}
                {statusCounts.not_started > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 bg-gray-300 rounded-full"></span> Not Started ({statusCounts.not_started})</span>}
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-gray-900">Items</div>
                <button
                  onClick={toggleSound}
                  className={`p-1.5 rounded transition-colors ${soundEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}
                  title={soundEnabled ? 'Sound enabled - click to disable' : 'Sound disabled - click to enable'}
                >
                  {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex items-center space-x-4 flex-1 ml-6">
                <div className="flex-1 max-w-lg relative">
                  <div className="relative">
                    <ScanLine className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Scan barcode or search by title, SKU, ASIN, FNSKU... (Enter to find & focus)"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && searchQuery.trim()) {
                          e.preventDefault();
                          handleScanSearch(searchQuery);
                        }
                      }}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="text-sm text-gray-600 whitespace-nowrap">
                  {filteredItems.length} of {inboundItems.length} items
                </div>
              </div>
            </div>

            {/* Status Filter Chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500 mr-1">Filter:</span>

              {/* All */}
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium transition-all duration-150 border ${
                  statusFilter === 'all'
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                All ({statusCounts.all})
              </button>

              {/* Pending (not fully received) */}
              {statusCounts.pending > 0 && (
                <button
                  onClick={() => setStatusFilter('pending')}
                  className={`px-3 py-1.5 text-xs font-medium transition-all duration-150 border ${
                    statusFilter === 'pending'
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-white text-amber-600 border-amber-300 hover:border-amber-400 hover:bg-amber-50'
                  }`}
                >
                  <Clock className="w-3 h-3 inline mr-1" />
                  Pending ({statusCounts.pending})
                </button>
              )}

              {/* Not Started */}
              {statusCounts.not_started > 0 && (
                <button
                  onClick={() => setStatusFilter('not_started')}
                  className={`px-3 py-1.5 text-xs font-medium transition-all duration-150 border ${
                    statusFilter === 'not_started'
                      ? 'bg-gray-500 text-white border-gray-500'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  Not Started ({statusCounts.not_started})
                </button>
              )}

              {/* Partial */}
              {statusCounts.partial > 0 && (
                <button
                  onClick={() => setStatusFilter('partial')}
                  className={`px-3 py-1.5 text-xs font-medium transition-all duration-150 border ${
                    statusFilter === 'partial'
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-white text-blue-600 border-blue-300 hover:border-blue-400 hover:bg-blue-50'
                  }`}
                >
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Partial ({statusCounts.partial})
                </button>
              )}

              {/* Received */}
              {statusCounts.received > 0 && (
                <button
                  onClick={() => setStatusFilter('received')}
                  className={`px-3 py-1.5 text-xs font-medium transition-all duration-150 border ${
                    statusFilter === 'received'
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-white text-green-600 border-green-300 hover:border-green-400 hover:bg-green-50'
                  }`}
                >
                  <CheckCircle2 className="w-3 h-3 inline mr-1" />
                  Received ({statusCounts.received})
                </button>
              )}

              {/* Over-received */}
              {statusCounts.over > 0 && (
                <button
                  onClick={() => setStatusFilter('over')}
                  className={`px-3 py-1.5 text-xs font-medium transition-all duration-150 border ${
                    statusFilter === 'over'
                      ? 'bg-purple-500 text-white border-purple-500'
                      : 'bg-white text-purple-600 border-purple-300 hover:border-purple-400 hover:bg-purple-50'
                  }`}
                >
                  <Plus className="w-3 h-3 inline mr-1" />
                  Over ({statusCounts.over})
                </button>
              )}

              {/* Clear filter button when not on 'all' */}
              {statusFilter !== 'all' && (
                <button
                  onClick={() => setStatusFilter('all')}
                  className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  title="Clear filter"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {/* Batch mode checkbox column */}
                  {batchShelfMode && (
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                      <input
                        type="checkbox"
                        checked={selectedForBatch.size === filteredItems.length && filteredItems.length > 0}
                        onChange={(e) => e.target.checked ? selectAllForBatch() : clearBatchSelection()}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                    </th>
                  )}
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-8">

                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">
                    Product Details
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Expected
                  </th>
                  <th className="px-1 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-8">

                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Actual
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                    Add to Shelf
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                    Current Shelves
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">

                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((item, index) => {
                  const originalIndex = inboundItems.findIndex(originalItem => originalItem.id === item.id);
                  const isPendingConfirmation = pendingConfirmation.has(item.id);
                  const itemStatus = getItemStatus(item);
                  const isHighlighted = highlightedItemId === item.id;
                  const isSelectedForBatch = selectedForBatch.has(item.id);

                  // Status indicator styles
                  const statusBorderColor = {
                    'not_started': 'border-l-gray-300',
                    'partial': 'border-l-blue-400',
                    'received': 'border-l-green-500',
                    'over': 'border-l-purple-500'
                  }[itemStatus] || 'border-l-gray-300';

                  return (
                    <tr
                      key={item.id}
                      id={`item-row-${item.id}`}
                      className={`hover:bg-gray-50 border-l-4 transition-all duration-300 ${statusBorderColor} ${
                        isPendingConfirmation ? 'bg-amber-50' :
                        isHighlighted ? 'bg-yellow-100 ring-2 ring-yellow-400' :
                        isSelectedForBatch ? 'bg-purple-50' : ''
                      }`}
                    >
                      {/* Batch mode checkbox */}
                      {batchShelfMode && (
                        <td className="px-2 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={isSelectedForBatch}
                            onChange={() => toggleBatchSelection(item.id)}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                        </td>
                      )}

                      {/* Status indicator */}
                      <td className="px-2 py-4 text-center">
                        <div className="flex flex-col items-center gap-1">
                          {itemStatus === 'received' && (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle2 className="w-4 h-4" />
                            </div>
                          )}
                          {itemStatus === 'partial' && (
                            <div className="flex items-center gap-1 text-blue-500">
                              <Clock className="w-4 h-4" />
                            </div>
                          )}
                          {itemStatus === 'over' && (
                            <div className="flex items-center gap-1 text-purple-600">
                              <Plus className="w-4 h-4" />
                            </div>
                          )}
                          {itemStatus === 'not_started' && (
                            <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-start space-x-3">
                          {item.item?.images?.[0]?.large_url ? (
                            <div className="relative">
                              <img 
                                src={item.item.images[0].large_url} 
                                alt={item.item.title}
                                className="w-12 h-12 object-cover flex-shrink-0 cursor-pointer"
                                onMouseEnter={() => setHoveredImage(item.id)}
                                onMouseLeave={() => setHoveredImage(null)}
                              />
                              {/* Large image tooltip */}
                              {hoveredImage === item.id && (
                                <div 
                                  className="fixed z-50 p-3 bg-white border-2 border-gray-400 shadow-xl rounded"
                                  style={{ 
                                    left: '50%',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    maxWidth: '90vw',
                                    maxHeight: '80vh'
                                  }}
                                >
                                  <img 
                                    src={item.item.images[0].large_url} 
                                    alt={item.item.title}
                                    className="w-96 h-96 object-contain"
                                    style={{ minWidth: '384px', minHeight: '384px' }}
                                  />
                                  <p className="text-sm text-gray-700 mt-3 max-w-96 text-center font-medium">
                                    {item.item.title}
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 line-clamp-2 mb-2">
                              {searchQuery ? highlightMatch(item.item?.title || 'Untitled Product', searchQuery) : (item.item?.title || 'Untitled Product')}
                            </p>
                            <div className="flex flex-wrap gap-x-3 gap-y-1">
                              <div className={`flex items-center space-x-1 ${fieldMatches(item.item?.merchant_sku, searchQuery) ? 'bg-yellow-100 -mx-1 px-1 rounded' : ''}`}>
                                <span className="text-xs text-gray-500 font-medium">SKU:</span>
                                <code className="text-xs bg-gray-100 px-1.5 py-0.5 font-mono">
                                  {searchQuery ? highlightMatch(item.item?.merchant_sku || 'N/A', searchQuery) : (item.item?.merchant_sku || 'N/A')}
                                </code>
                              </div>
                              <div className={`flex items-center space-x-1 ${fieldMatches(item.item?.asin, searchQuery) ? 'bg-yellow-100 -mx-1 px-1 rounded' : ''}`}>
                                <span className="text-xs text-gray-500 font-medium">ASIN:</span>
                                <code className="text-xs bg-gray-100 px-1.5 py-0.5 font-mono">
                                  {searchQuery ? highlightMatch(item.item?.asin || 'N/A', searchQuery) : (item.item?.asin || 'N/A')}
                                </code>
                              </div>
                              <div className={`flex items-center space-x-1 ${fieldMatches(item.item?.fnsku, searchQuery) ? 'bg-yellow-100 -mx-1 px-1 rounded' : ''}`}>
                                <span className="text-xs text-gray-500 font-medium">FNSKU:</span>
                                <code className="text-xs bg-gray-100 px-1.5 py-0.5 font-mono">
                                  {searchQuery ? highlightMatch(item.item?.fnsku || 'N/A', searchQuery) : (item.item?.fnsku || 'N/A')}
                                </code>
                              </div>
                              {/* Show matching barcode/GTIN if search matched searchableIdentifiers */}
                              {searchQuery && fieldMatches(item.item?.searchableIdentifiers, searchQuery) && !fieldMatches(item.item?.merchant_sku, searchQuery) && !fieldMatches(item.item?.asin, searchQuery) && !fieldMatches(item.item?.fnsku, searchQuery) && !fieldMatches(item.item?.title, searchQuery) && (
                                <div className="flex items-center space-x-1 bg-yellow-100 -mx-1 px-1 rounded">
                                  <span className="text-xs text-yellow-700 font-medium">GTIN:</span>
                                  <code className="text-xs bg-yellow-50 px-1.5 py-0.5 font-mono text-yellow-800">
                                    {(() => {
                                      // Find and show the matching barcode
                                      const barcodes = item.item?.searchableIdentifiers?.split(',') || [];
                                      const matchingBarcode = barcodes.find(b => b.trim().toLowerCase().includes(searchQuery.toLowerCase()));
                                      return matchingBarcode ? highlightMatch(matchingBarcode.trim(), searchQuery) : searchQuery;
                                    })()}
                                  </code>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-3 py-4">
                        <span className="text-sm font-medium text-gray-900">
                          {item.expected?.quantity || 0}
                        </span>
                      </td>
                      
                      <td className="px-1 py-4 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetActualToExpected(originalIndex)}
                          className="p-1.5 hover:bg-blue-50 border-blue-300 text-blue-600 hover:text-blue-800"
                          title="Set actual = expected"
                        >
                          <ArrowRight className="w-3 h-3" />
                        </Button>
                      </td>
                      
                      <td className="px-3 py-4">
                        <div className="flex items-center space-x-1">
                          <input
                            id={`actual-input-${originalIndex}`}
                            type="number"
                            value={actualInputValues[originalIndex] !== undefined ? actualInputValues[originalIndex] : (item.actual?.quantity || 0)}
                            onChange={(e) => {
                              // Update local state only while typing
                              setActualInputValues(prev => ({
                                ...prev,
                                [originalIndex]: e.target.value
                              }));
                            }}
                            onBlur={(e) => {
                              const newValue = parseInt(e.target.value) || 0;
                              const currentValue = item.actual?.quantity || 0;

                              // Only update if the value actually changed
                              if (newValue !== currentValue) {
                                handleActualQuantityChange(originalIndex, newValue);
                              }

                              // Clear the local input value to use the actual state again
                              setActualInputValues(prev => {
                                const updated = { ...prev };
                                delete updated[originalIndex];
                                return updated;
                              });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const newValue = parseInt(e.target.value) || 0;
                                const currentValue = item.actual?.quantity || 0;

                                // Save if changed, then move to shelf code input
                                if (newValue !== currentValue) {
                                  handleActualQuantityChange(originalIndex, newValue);
                                }

                                // Move to shelf code input
                                setTimeout(() => {
                                  const shelfInput = document.getElementById(`shelf-input-${originalIndex}`);
                                  if (shelfInput) shelfInput.focus();
                                }, 50);
                              }
                            }}
                            className="w-14 px-2 py-1 text-sm border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                          />
                          <span className="text-gray-400 text-sm">+</span>
                          <input
                            type="number"
                            value={quickAddInputs[originalIndex] || ''}
                            onChange={(e) => setQuickAddInputs(prev => ({
                              ...prev,
                              [originalIndex]: e.target.value
                            }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleQuickAdd(originalIndex);
                                // Move to shelf code input after adding
                                setTimeout(() => {
                                  const shelfInput = document.getElementById(`shelf-input-${originalIndex}`);
                                  if (shelfInput) shelfInput.focus();
                                }, 100);
                              }
                            }}
                            placeholder="+/-"
                            className="w-12 px-1 py-1 text-sm border-2 border-green-400 focus:outline-none focus:ring-2 focus:ring-green-500 placeholder-green-400"
                          />
                        </div>
                        {/* Condensed recent changes for this item (last 3) */}
                        {(() => {
                          const recentChanges = getRecentChangesForSku(item.item?.merchant_sku, 3);
                          return recentChanges.length > 0 ? (
                            <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                              {recentChanges.map((change, idx) => {
                                const diff = change.newValue - change.oldValue;
                                const diffStr = diff > 0 ? `+${diff}` : `${diff}`;
                                return (
                                  <div key={change.id} className="flex items-center space-x-1">
                                    <span className="text-gray-500">{change.oldValue}</span>
                                    <span className="text-gray-400">→</span>
                                    <span className={`font-medium ${
                                      diff > 0 ? 'text-green-600' :
                                      diff < 0 ? 'text-red-600' : 'text-gray-600'
                                    }`}>
                                      {change.newValue}
                                    </span>
                                    <span className={`${
                                      diff > 0 ? 'text-green-500' :
                                      diff < 0 ? 'text-red-500' : 'text-gray-400'
                                    }`}>
                                      ({diffStr})
                                    </span>
                                    <span className="text-gray-400">{formatTimeAgo(change.timestamp)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : null;
                        })()}
                      </td>
                      
                      <td className={`px-4 py-4 transition-all duration-300 ${
                        shelfSuccessAnimation[originalIndex] ? 'bg-green-50' : ''
                      }`}>
                        <div className="w-full max-w-sm">
                          {/* Main input row */}
                          <div className="flex items-center space-x-2 mb-2">
                            {/* Shelf code input with suggestions */}
                            <div className="relative flex-1">
                              <input
                                id={`shelf-input-${originalIndex}`}
                                type="text"
                                placeholder="Shelf Code"
                                value={shelfQuantities[originalIndex]?.code || ''}
                                onChange={(e) => {
                                  const newCode = e.target.value.toUpperCase();
                                  setShelfQuantities(prev => {
                                    const currentQty = prev[originalIndex]?.quantity;
                                    let autoQty = currentQty;

                                    // Auto-fill quantity when entering shelf code (if no qty set yet)
                                    if (!currentQty && newCode) {
                                      // Get the most recent change to find the last increment
                                      const recentChanges = getRecentChangesForSku(item.item?.merchant_sku, 1);
                                      if (recentChanges.length > 0) {
                                        const lastChange = recentChanges[0];
                                        const increment = lastChange.newValue - lastChange.oldValue;
                                        // Use the increment if it's positive (added items)
                                        if (increment > 0) {
                                          autoQty = increment.toString();
                                        } else {
                                          // If negative or no change, use actual quantity
                                          autoQty = (item.actual?.quantity || 0).toString();
                                        }
                                      } else {
                                        // No recent changes, use actual quantity
                                        autoQty = (item.actual?.quantity || 0).toString();
                                      }
                                    }

                                    return {
                                      ...prev,
                                      [originalIndex]: { code: newCode, quantity: autoQty || '' }
                                    };
                                  });
                                }}
                                onFocus={() => {
                                  setFocusedShelfInput(originalIndex);
                                  // Auto-fill quantity when focusing shelf code input (if no qty set yet)
                                  setShelfQuantities(prev => {
                                    const currentQty = prev[originalIndex]?.quantity;
                                    if (currentQty) return prev; // Already has quantity, don't override

                                    // Get the most recent change to find the last increment
                                    const recentChanges = getRecentChangesForSku(item.item?.merchant_sku, 1);
                                    let autoQty = '';
                                    if (recentChanges.length > 0) {
                                      const lastChange = recentChanges[0];
                                      const increment = lastChange.newValue - lastChange.oldValue;
                                      // Use the increment if it's positive (added items)
                                      if (increment > 0) {
                                        autoQty = increment.toString();
                                      } else {
                                        autoQty = (item.actual?.quantity || 0).toString();
                                      }
                                    } else {
                                      autoQty = (item.actual?.quantity || 0).toString();
                                    }

                                    return {
                                      ...prev,
                                      [originalIndex]: { ...prev[originalIndex], quantity: autoQty }
                                    };
                                  });
                                }}
                                onBlur={() => setTimeout(() => setFocusedShelfInput(null), 200)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (shelfQuantities[originalIndex]?.code && shelfQuantities[originalIndex]?.quantity) {
                                      handleAddToShelf(originalIndex, shelfQuantities[originalIndex].code, shelfQuantities[originalIndex].quantity);
                                      // Move to next row's actual input after adding
                                      setTimeout(() => {
                                        const nextIndex = originalIndex + 1;
                                        const nextActualInput = document.getElementById(`actual-input-${nextIndex}`);
                                        if (nextActualInput) {
                                          nextActualInput.focus();
                                          nextActualInput.select();
                                        }
                                      }, 100);
                                    } else {
                                      // Move to shelf qty input if shelf code entered but no qty
                                      const qtyInput = document.getElementById(`shelf-qty-input-${originalIndex}`);
                                      if (qtyInput) qtyInput.focus();
                                    }
                                  }
                                }}
                                className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors font-mono"
                              />
                              
                              {/* Shelf suggestions dropdown */}
                              {focusedShelfInput === originalIndex && (
                                <div className="fixed z-50 mt-1 w-48 bg-white border border-gray-300 shadow-lg max-h-48 overflow-y-auto"
                                     style={{
                                       top: `${document.getElementById(`shelf-input-${originalIndex}`)?.getBoundingClientRect().bottom + window.scrollY || 0}px`,
                                       left: `${document.getElementById(`shelf-input-${originalIndex}`)?.getBoundingClientRect().left + window.scrollX || 0}px`
                                     }}>
                                  {/* Current shelves */}
                                  {shelfData[item.item?.merchant_sku] && shelfData[item.item?.merchant_sku].length > 0 && (
                                    <>
                                      <div className="px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-50 border-b">Current Shelves</div>
                                      {shelfData[item.item?.merchant_sku].map((shelf, idx) => (
                                        <button
                                          key={`current-${idx}`}
                                          onClick={() => {
                                            setShelfQuantities(prev => ({
                                              ...prev,
                                              [originalIndex]: { 
                                                ...prev[originalIndex],
                                                code: shelf.FULL_SHELF_CODE
                                              }
                                            }));
                                            setFocusedShelfInput(null);
                                          }}
                                          className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors"
                                        >
                                          <div className="flex items-center justify-between">
                                            <span className="font-mono font-medium text-blue-700 text-sm">
                                              {shelf.FULL_SHELF_CODE}
                                            </span>
                                            <span className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold">
                                              {shelf.QTY}
                                            </span>
                                          </div>
                                        </button>
                                      ))}
                                    </>
                                  )}
                                  
                                  {/* Recent shelves */}
                                  {recentShelves.length > 0 && (
                                    <>
                                      {shelfData[item.item?.merchant_sku]?.length > 0 && <div className="border-t border-gray-200" />}
                                      <div className="px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-50 border-b">Recent</div>
                                      {recentShelves.slice(0, 4).map((shelf, idx) => {
                                        const shelfCode = typeof shelf === 'string' ? shelf : shelf.code;
                                        const isAlreadyCurrent = shelfData[item.item?.merchant_sku]?.some(s => s.FULL_SHELF_CODE === shelfCode);
                                        if (isAlreadyCurrent) return null;
                                        return (
                                          <button
                                            key={`recent-${idx}`}
                                            onClick={() => {
                                              setShelfQuantities(prev => ({
                                                ...prev,
                                                [originalIndex]: { 
                                                  ...prev[originalIndex],
                                                  code: shelfCode
                                                }
                                              }));
                                              setFocusedShelfInput(null);
                                            }}
                                            className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                                          >
                                            <span className="font-mono text-gray-700 text-sm">{shelfCode}</span>
                                          </button>
                                        );
                                      })}
                                    </>
                                  )}
                                  
                                  {(!shelfData[item.item?.merchant_sku] || shelfData[item.item?.merchant_sku].length === 0) && recentShelves.length === 0 && (
                                    <div className="px-3 py-4 text-center text-sm text-gray-500">
                                      No suggestions available
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Quantity input */}
                            <div className="w-20">
                              <input
                                id={`shelf-qty-input-${originalIndex}`}
                                type="number"
                                placeholder="Qty"
                                value={shelfQuantities[originalIndex]?.quantity || ''}
                                onChange={(e) => setShelfQuantities(prev => ({
                                  ...prev,
                                  [originalIndex]: { ...prev[originalIndex], quantity: e.target.value }
                                }))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && shelfQuantities[originalIndex]?.code && shelfQuantities[originalIndex]?.quantity) {
                                    e.preventDefault();
                                    handleAddToShelf(originalIndex, shelfQuantities[originalIndex].code, shelfQuantities[originalIndex].quantity);
                                    // Move to next row's actual input after adding
                                    setTimeout(() => {
                                      const nextIndex = originalIndex + 1;
                                      const nextActualInput = document.getElementById(`actual-input-${nextIndex}`);
                                      if (nextActualInput) {
                                        nextActualInput.focus();
                                        nextActualInput.select();
                                      }
                                    }, 100);
                                  }
                                }}
                                className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                              />
                              {/* Set actual quantity button underneath */}
                              {item.actual?.quantity > 0 && (
                                <button
                                  onClick={() => {
                                    setShelfQuantities(prev => ({
                                      ...prev,
                                      [originalIndex]: { ...prev[originalIndex], quantity: (item.actual?.quantity || 0).toString() }
                                    }));
                                  }}
                                  className="w-full text-xs px-2 py-1 mt-1 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 transition-colors"
                                  title="Set quantity to actual amount"
                                >
                                  Set {item.actual?.quantity}
                                </button>
                              )}
                            </div>
                            
                            {/* Add button */}
                            <Button
                              size="sm"
                              onClick={() => handleAddToShelf(
                                originalIndex, 
                                shelfQuantities[originalIndex]?.code,
                                shelfQuantities[originalIndex]?.quantity
                              )}
                              disabled={!shelfQuantities[originalIndex]?.code || !shelfQuantities[originalIndex]?.quantity}
                              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 font-medium transition-colors"
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {shelfData[item.item?.merchant_sku] && shelfData[item.item?.merchant_sku].length > 0 ? (
                            shelfData[item.item?.merchant_sku].map((shelf, shelfIndex) => {
                              const shelfKey = `${item.item?.merchant_sku}-${shelfIndex}`;
                              return (
                                <div key={shelfIndex} className="inline-block">
                                  {editingShelf === shelfKey ? (
                                    <div className="flex items-center bg-blue-50 border border-blue-300 px-1 py-0.5">
                                      <code className="text-xs text-blue-800 font-mono mr-1">
                                        {shelf.FULL_SHELF_CODE}
                                      </code>
                                      <span className="text-xs text-gray-500 mr-1">×</span>
                                      <input
                                        type="number"
                                        value={editShelfQty}
                                        onChange={(e) => setEditShelfQty(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            handleUpdateShelfQty(shelf, editShelfQty);
                                          }
                                          if (e.key === 'Escape') {
                                            setEditingShelf(null);
                                          }
                                        }}
                                        onBlur={() => {
                                          const qty = parseInt(editShelfQty);
                                          if (!isNaN(qty) && qty >= 0) {
                                            handleUpdateShelfQty(shelf, editShelfQty);
                                          } else {
                                            setEditingShelf(null);
                                          }
                                        }}
                                        className="w-12 px-1 py-0.5 text-xs border border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        autoFocus
                                      />
                                      <button
                                        onClick={() => handleUpdateShelfQty(shelf, 0)}
                                        className="text-red-500 hover:text-red-700 text-xs ml-1 font-bold"
                                        title="Remove from shelf"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        setEditingShelf(shelfKey);
                                        setEditShelfQty(shelf.QTY.toString());
                                      }}
                                      className="inline-flex items-center bg-blue-50 hover:bg-blue-100 border border-blue-200 px-2 py-0.5 transition-colors group"
                                      title="Click to edit"
                                    >
                                      <code className="text-xs text-blue-800 font-mono">
                                        {shelf.FULL_SHELF_CODE}
                                      </code>
                                      <span className="text-xs text-gray-400 mx-1">×</span>
                                      <span className="text-xs font-semibold text-blue-900">{shelf.QTY}</span>
                                      <Edit3 className="w-3 h-3 ml-1 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </button>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <span className="text-xs text-gray-400 italic">-</span>
                          )}
                        </div>
                      </td>

                      {/* Done button column - shows for items pending confirmation */}
                      <td className="px-3 py-4 text-center">
                        {pendingConfirmation.has(item.id) && (
                          <div className="flex flex-col items-center space-y-1">
                            <Button
                              size="sm"
                              onClick={() => handleConfirmItemDone(item.id)}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs font-medium flex items-center space-x-1"
                              title="Click when done with this item to hide it from the filter"
                            >
                              <CheckCircle2 className="w-3 h-3" />
                              <span>Done</span>
                            </Button>
                            <span className="text-xs text-amber-600 font-medium">Ready to hide</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Add All to Shelf Dialog */}
        {openShelfDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-xl font-semibold text-gray-900">Add All Items to Shelf</div>
                  <button
                    onClick={() => setOpenShelfDialog(false)}
                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                
                <div className="space-y-4">
                  {/* Shelf Code Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shelf Code
                    </label>
                    <div className="flex space-x-2 mb-2">
                      <Input
                        placeholder="Enter shelf code (e.g., P-01-01)"
                        value={allShelfCode}
                        onChange={(e) => setAllShelfCode(e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        variant="outline"
                        onClick={() => setAllShelfCode('P-01-01')}
                      >
                        P-01-01
                      </Button>
                    </div>
                    
                    {/* Recent Shelves */}
                    {recentShelves.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Recent shelves:</p>
                        <div className="flex flex-wrap gap-1">
                          {recentShelves.map((shelf, idx) => (
                            <button
                              key={idx}
                              onClick={() => setAllShelfCode(shelf)}
                              className="px-2 py-1 text-xs bg-gray-100 text-gray-700  hover:bg-gray-200"
                            >
                              {shelf}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Items List */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-700">Items to add ({filteredItems.length} items):</div>
                      <div className="text-xs text-gray-500">SKU • Quantity</div>
                    </div>
                    
                    <div className="max-h-96 overflow-y-auto space-y-3 border border-gray-200 rounded-lg p-4 bg-gray-50">
                      {filteredItems.map((item, filteredIndex) => {
                        const originalIndex = inboundItems.findIndex(originalItem => originalItem.id === item.id);
                        return (
                          <div key={item.id} className="flex items-start gap-4 p-4 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                            {/* Product Image */}
                            <div className="flex-shrink-0">
                              {item.item?.images?.[0]?.large_url ? (
                                <img 
                                  src={item.item.images[0].large_url} 
                                  alt={item.item?.title}
                                  className="w-12 h-12 rounded object-cover"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                  <Package className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                            
                            {/* Product Details */}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">
                                {item.item?.title || 'Untitled Product'}
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                                <span><span className="font-medium">SKU:</span> {item.item?.merchant_sku || 'N/A'}</span>
                                <span><span className="font-medium">ASIN:</span> {item.item?.asin || 'N/A'}</span>
                                <span><span className="font-medium">Expected:</span> {item.expected?.quantity || 0}</span>
                              </div>
                            </div>
                            
                            {/* Quantity Input */}
                            <div className="flex-shrink-0">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={dialogQuantities[originalIndex] !== undefined ? dialogQuantities[originalIndex] : (item.expected?.quantity || 0)}
                                  onChange={(e) => setDialogQuantities(prev => ({
                                    ...prev,
                                    [originalIndex]: parseInt(e.target.value) || 0
                                  }))}
                                  className="w-24 px-3 py-2.5 text-sm border-2 border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400 font-medium text-center"
                                  placeholder="0"
                                  min="0"
                                />
                                <div className="absolute -bottom-5 left-0 text-xs text-gray-400">
                                  Expected: {item.expected?.quantity || 0}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Fixed Footer */}
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    {(() => {
                      const itemsWithQuantities = filteredItems.filter((item, filteredIndex) => {
                        const originalIndex = inboundItems.findIndex(originalItem => originalItem.id === item.id);
                        const quantity = dialogQuantities[originalIndex] !== undefined ? dialogQuantities[originalIndex] : (item.expected?.quantity || 0);
                        return quantity > 0;
                      });
                      return itemsWithQuantities.length;
                    })()} items with quantities set
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => setOpenShelfDialog(false)}
                      size="lg"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAddAllToShelf}
                      disabled={!allShelfCode || (() => {
                        const itemsWithQuantities = filteredItems.filter((item, filteredIndex) => {
                          const originalIndex = inboundItems.findIndex(originalItem => originalItem.id === item.id);
                          const quantity = dialogQuantities[originalIndex] !== undefined ? dialogQuantities[originalIndex] : (item.expected?.quantity || 0);
                          return quantity > 0;
                        });
                        return itemsWithQuantities.length === 0;
                      })()}
                      size="lg"
                      className="px-6"
                    >
                      Add {(() => {
                        const itemsWithQuantities = filteredItems.filter((item, filteredIndex) => {
                          const originalIndex = inboundItems.findIndex(originalItem => originalItem.id === item.id);
                          const quantity = dialogQuantities[originalIndex] !== undefined ? dialogQuantities[originalIndex] : (item.expected?.quantity || 0);
                          return quantity > 0;
                        });
                        return itemsWithQuantities.length;
                      })()} Items to Shelf
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Contact Selection Modal */}
        {isContactSelectModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowContactDropdown(false)}
          >
            <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-lg font-semibold text-gray-900">Select Contact</div>
                  <button
                    onClick={() => setIsContactSelectModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search and select contact to email:
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Search clients by name or email..."
                      value={contactSearchQuery}
                      onChange={(e) => {
                        setContactSearchQuery(e.target.value);
                        setShowContactDropdown(true);
                      }}
                      onFocus={() => setShowContactDropdown(true)}
                      className="w-full"
                      icon={Search}
                    />

                    {/* Searchable Dropdown */}
                    {showContactDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 shadow-lg max-h-60 overflow-y-auto">
                        {allClients
                          .filter(clientItem =>
                            clientItem.email &&
                            clientItem.email.trim() !== '' &&
                            (clientItem.name.toLowerCase().includes(contactSearchQuery.toLowerCase()) ||
                             clientItem.email.toLowerCase().includes(contactSearchQuery.toLowerCase()))
                          )
                          .map(clientItem => (
                            <button
                              key={clientItem.id}
                              onClick={() => {
                                setSelectedContactEmail(clientItem.email);
                                setContactSearchQuery(`${clientItem.name} (${clientItem.email})`);
                                setShowContactDropdown(false);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex flex-col">
                                <span className="font-medium text-gray-900">{clientItem.name}</span>
                                <span className="text-sm text-gray-600">{clientItem.email}</span>
                              </div>
                            </button>
                          ))
                        }
                        {allClients.filter(clientItem =>
                          clientItem.email &&
                          clientItem.email.trim() !== '' &&
                          (clientItem.name.toLowerCase().includes(contactSearchQuery.toLowerCase()) ||
                           clientItem.email.toLowerCase().includes(contactSearchQuery.toLowerCase()))
                        ).length === 0 && (
                          <div className="px-3 py-2 text-gray-500 text-center">
                            No clients found
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Display selected contact */}
                  {selectedContactEmail && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 text-sm">
                      <span className="font-medium text-blue-800">Selected: </span>
                      <span className="text-blue-700">{contactSearchQuery}</span>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    onClick={() => setIsContactSelectModalOpen(false)}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (selectedContactEmail) {
                        setIsContactSelectModalOpen(false);
                        setIsEmailModalOpen(true);
                      }
                    }}
                    disabled={!selectedContactEmail}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Continue to Email
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Email Modal */}
        {isEmailModalOpen && selectedContactEmail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-lg font-semibold text-gray-900">Contact Client</div>
                  <button
                    onClick={() => setIsEmailModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <SendEmailInbound
                  recipientEmail={selectedContactEmail}
                  onClose={() => setIsEmailModalOpen(false)}
                  sendWithInboundInfo={true}
                  inboundInfo={inboundInfo}
                />
              </div>
            </Card>
          </div>
        )}

        {/* Sticky Quick Actions Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-lg z-40">
          <div className="max-w-full mx-auto px-6 py-3">
            <div className="flex items-center justify-between gap-6">
              {/* Progress Summary */}
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                    <span className="text-gray-600">Pending: <span className="font-semibold text-gray-900">{statusCounts.pending || 0}</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-gray-600">Received: <span className="font-semibold text-green-700">{statusCounts.received || 0}</span></span>
                  </div>
                  {statusCounts.over > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <span className="text-gray-600">Over: <span className="font-semibold text-purple-700">{statusCounts.over}</span></span>
                    </div>
                  )}
                </div>

                {/* Mini Progress Bar */}
                <div className="w-32 bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div className="h-full flex">
                    <div
                      className="bg-green-500"
                      style={{ width: `${statusCounts.all > 0 ? ((statusCounts.received) / statusCounts.all) * 100 : 0}%` }}
                    />
                    <div
                      className="bg-purple-500"
                      style={{ width: `${statusCounts.all > 0 ? ((statusCounts.over || 0) / statusCounts.all) * 100 : 0}%` }}
                    />
                  </div>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {statusCounts.all > 0 ? Math.round(((statusCounts.received + (statusCounts.over || 0)) / statusCounts.all) * 100) : 0}%
                </span>
              </div>

              {/* Quick Actions */}
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleSetAllActualToExpected}
                  variant="outline"
                  size="sm"
                  className="text-green-700 border-green-300 hover:bg-green-50 hover:border-green-400"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Set All = Expected
                </Button>

                {showReceivedBanner && (
                  <Button
                    onClick={handleMarkShipmentAsReceived}
                    className="bg-green-600 hover:bg-green-700 text-white animate-pulse"
                    size="sm"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Mark Shipment Received
                  </Button>
                )}

                <Button
                  onClick={() => {
                    if (client?.email) {
                      setSelectedContactEmail(client.email);
                      setContactSearchQuery(`${client.name} (${client.email})`);
                    }
                    setIsContactSelectModalOpen(true);
                  }}
                  variant="outline"
                  size="sm"
                  className="text-blue-700 border-blue-300 hover:bg-blue-50"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact
                </Button>

                <a
                  href={`https://app.yyzprep.ca/staff/shipments/inbound/${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  YYZ
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}

export default InboundDetail;